<script lang="ts">
  import { onMount } from 'svelte';
  import { graphql } from '../lib/api/gh';
  import { failureOf, type Failure } from '../lib/api/failure';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import { oneStep } from '../lib/loading/steps';
  import { REPO_PULLS_QUERY, type Actor } from '../lib/api/queries';
  import { relTime } from '../lib/logic/time';
  import { prPath } from '../lib/router';

  interface Props {
    owner: string;
    repo: string;
  }
  let { owner, repo }: Props = $props();

  interface Pull {
    number: number;
    title: string;
    isDraft: boolean;
    updatedAt: string;
    additions: number;
    deletions: number;
    reviewDecision: string | null;
    author: Actor | null;
    commits: { nodes: { commit: { statusCheckRollup: { state: string } | null } }[] };
  }

  let pulls: Pull[] = $state([]);
  let status: 'loading' | 'ready' | 'error' | 'notfound' = $state('loading');
  let failure: Failure | null = $state(null);

  async function load() {
    status = 'loading';
    try {
      const r = await graphql<{ repository: { pullRequests: { nodes: Pull[] } } | null }>(REPO_PULLS_QUERY, { owner, repo });
      if (!r.repository) status = 'notfound';
      else {
        pulls = r.repository.pullRequests.nodes;
        status = 'ready';
      }
    } catch (e) {
      failure = failureOf(e);
      status = 'error';
    }
  }

  onMount(() => void load());

  const dot = (p: Pull) => {
    const s = p.commits.nodes[0]?.commit.statusCheckRollup?.state;
    return s === 'SUCCESS' ? 'good' : s === 'FAILURE' || s === 'ERROR' ? 'bad' : s ? 'pending' : '';
  };
</script>

<LoadingGate
  phase={status === 'loading' ? 'loading' : status === 'error' && failure ? 'failed' : 'ready'}
  view={oneStep(`${owner}/${repo}`, 'Open pull requests', status === 'error' && failure ? { failure, what: `the open pull requests of ${owner}/${repo}` } : 'loading')}
  onretry={load}
>
{#if status === 'notfound'}
  <div class="center-msg"><h1>Repository not found</h1><p class="muted">GitHub doesn't show {owner}/{repo} to you.</p></div>
{:else}
  <ul class="pr-list">
    {#each pulls as p (p.number)}
      <li class="pr-row">
        <span class="dot {dot(p)}"></span>
        <div class="pr-row-main">
          <a class="pr-row-title" href={prPath(owner, repo, p.number)}>{p.title}</a>
          <div class="pr-row-sub">
            <span class="mono">#{p.number}</span><span>by {p.author?.login ?? 'ghost'}</span><span>updated {relTime(p.updatedAt)}</span>
            {#if p.isDraft}<span class="pill neutral">draft</span>{/if}
            {#if p.reviewDecision === 'APPROVED'}<span class="pill">approved</span>{/if}
          </div>
        </div>
        <div class="pr-row-delta mono"><span class="positive">+{p.additions}</span> <span class="negative">−{p.deletions}</span></div>
      </li>
    {:else}
      <li class="muted empty-line">No open pull requests.</li>
    {/each}
  </ul>
{/if}
</LoadingGate>
